import React, { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { cssMap } from '@atlaskit/css';
import { graphql } from 'react-relay';

import { IconButton } from '@atlaskit/button/new';
import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import CrossIcon from '@atlaskit/icon/core/cross';
import EditIcon from '@atlaskit/icon/core/edit';
import TextArea from '@atlaskit/textarea';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { toDocumentNode } from '@confluence/apollo-relay-interop/entry-points/toDocumentNode.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type {
	SmartSpaceOverviewSummaryQuery$data,
	SmartSpaceOverviewSummaryQuery$variables,
} from './__generated__/SmartSpaceOverviewSummaryQuery.graphql.ts';
import type {
	SmartSpaceOverviewSummaryUpdateMutation$data,
	SmartSpaceOverviewSummaryUpdateMutation$variables,
} from './__generated__/SmartSpaceOverviewSummaryUpdateMutation.graphql.ts';

type SmartSpaceOverviewSummaryViewProps = {
	body: string;
	onSave: (newBody: string) => void;
};

export const SmartSpaceOverviewSummaryView = ({
	body,
	onSave,
}: SmartSpaceOverviewSummaryViewProps) => {
	const { formatMessage } = useIntl();
	const [isHovered, setIsHovered] = useState(false);
	const [isEditing, setIsEditing] = useState(false);
	const [editText, setEditText] = useState(body);

	const handleMouseEnter = useCallback(() => setIsHovered(true), []);
	const handleMouseLeave = useCallback(() => setIsHovered(false), []);

	const onEdit = useCallback(() => {
		setEditText(body);
		setIsEditing(true);
	}, [body]);

	const onConfirm = useCallback(() => {
		onSave(editText);
		setIsEditing(false);
	}, [editText, onSave]);

	const onCancel = useCallback(() => {
		setEditText(body);
		setIsEditing(false);
	}, [body]);

	const onKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
			if (e.key === 'Escape') {
				onCancel();
			}
		},
		[onCancel],
	);

	if (isEditing) {
		return (
			<div css={styles.editContainer} data-testid="smart-space-overview-summary-edit">
				<TextArea
					value={editText}
					onChange={(e) => setEditText(e.target.value)}
					onKeyDown={onKeyDown}
					minimumRows={3}
					resize="vertical"
					testId="smart-space-overview-summary-edit-input"
				/>
				<div css={styles.editActions}>
					<Tooltip content={formatMessage(i18n.confirm)}>
						<IconButton
							icon={CheckMarkIcon}
							label={formatMessage(i18n.confirm)}
							appearance="default"
							onClick={onConfirm}
							testId="smart-space-overview-summary-confirm-button"
						/>
					</Tooltip>
					<Tooltip content={formatMessage(i18n.cancel)}>
						<IconButton
							icon={CrossIcon}
							label={formatMessage(i18n.cancel)}
							appearance="default"
							onClick={onCancel}
							testId="smart-space-overview-summary-cancel-button"
						/>
					</Tooltip>
				</div>
			</div>
		);
	}

	return (
		<div
			css={styles.summaryContainer}
			data-testid="smart-space-overview-summary"
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			onFocus={handleMouseEnter}
			onBlur={handleMouseLeave}
		>
			<div css={styles.summaryBody}>{body}</div>
			{isHovered && (
				<Tooltip content={formatMessage(i18n.edit)}>
					<IconButton
						icon={EditIcon}
						label={formatMessage(i18n.edit)}
						appearance="subtle"
						spacing="compact"
						onClick={onEdit}
						testId="smart-space-overview-summary-edit-button"
					/>
				</Tooltip>
			)}
		</div>
	);
};

type SmartSpaceOverviewSummaryProps = {
	spaceKey: string;
};

export const SmartSpaceOverviewSummary = ({ spaceKey }: SmartSpaceOverviewSummaryProps) => {
	const { cloudId } = useSessionData();
	const { data } = useQueryInterop<
		SmartSpaceOverviewSummaryQuery$data,
		SmartSpaceOverviewSummaryQuery$variables
	>(SmartSpaceOverviewSummaryQuery, {
		variables: {
			cloudId,
			spaceKey,
		},
		skip: !cloudId,
	});

	const queryDocumentNode = toDocumentNode(SmartSpaceOverviewSummaryQuery);

	const [updateSummary] = useMutationInterop<
		SmartSpaceOverviewSummaryUpdateMutation$data,
		SmartSpaceOverviewSummaryUpdateMutation$variables
	>(UpdateSmartSpaceOverviewSummaryMutation);

	const onSave = useCallback(
		(newBody: string) => {
			void updateSummary({
				variables: {
					cloudId,
					input: {
						spaceKey,
						summary: {
							body: newBody,
						},
					},
				},
				optimisticResponse: {
					confluence_updateSmartSpaceOverview: {
						success: true,
						errors: null,
						overview: {
							summary: {
								body: newBody,
							},
						},
					},
				},
				update: (cache, { data: mutationData }) => {
					const updatedBody =
						mutationData?.confluence_updateSmartSpaceOverview?.overview?.summary?.body;
					if (updatedBody == null) {
						return;
					}
					cache.writeQuery({
						query: queryDocumentNode,
						variables: { cloudId, spaceKey },
						data: {
							confluence_smartSpaceOverview: {
								__typename: 'ConfluenceSmartSpaceOverview',
								summary: {
									__typename: 'ConfluenceSmartSpaceOverviewSummary',
									body: updatedBody,
								},
							},
						},
					});
				},
			});
		},
		[cloudId, spaceKey, updateSummary, queryDocumentNode],
	);

	const body = data?.confluence_smartSpaceOverview?.summary?.body;

	if (!body) {
		return null;
	}

	return <SmartSpaceOverviewSummaryView body={body} onSave={onSave} />;
};

const SmartSpaceOverviewSummaryQuery = graphql`
	query SmartSpaceOverviewSummaryQuery($cloudId: ID!, $spaceKey: String!) {
		confluence_smartSpaceOverview(cloudId: $cloudId, spaceKey: $spaceKey) {
			summary {
				body
			}
		}
	}
`;

const UpdateSmartSpaceOverviewSummaryMutation = graphql`
	mutation SmartSpaceOverviewSummaryUpdateMutation(
		$cloudId: ID!
		$input: ConfluenceUpdateSmartSpaceOverviewInput!
	) {
		confluence_updateSmartSpaceOverview(cloudId: $cloudId, input: $input) {
			success
			errors {
				message
			}
			overview {
				summary {
					body
				}
			}
		}
	}
`;

const i18n = defineMessages({
	edit: {
		id: 'space-overview.smart-space-overview.summary.edit',
		defaultMessage: 'Edit',
		description: 'Tooltip and label for the edit button on the space overview summary',
	},
	confirm: {
		id: 'space-overview.smart-space-overview.summary.confirm',
		defaultMessage: 'Confirm',
		description: 'Tooltip and label for the confirm button when editing the space overview summary',
	},
	cancel: {
		id: 'space-overview.smart-space-overview.summary.cancel',
		defaultMessage: 'Cancel',
		description: 'Tooltip and label for the cancel button when editing the space overview summary',
	},
});

const styles = cssMap({
	summaryContainer: {
		display: 'flex',
		alignItems: 'flex-start',
		gap: token('space.100'),
	},
	summaryBody: {
		color: token('color.text'),
		font: token('font.body'),
		whiteSpace: 'pre-wrap',
		flex: 1,
	},
	editContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.050'),
		backgroundColor: token('color.background.neutral.subtle'),
		borderRadius: token('radius.medium'),
		padding: token('space.050'),
	},
	editActions: {
		display: 'flex',
		justifyContent: 'flex-end',
		alignItems: 'center',
		gap: token('space.050'),
	},
});
